import gql from "graphql-tag";

// minimal property shape for the top-lists (clickable rows)
const PROP = `
  id
  propertyType
  fullAddress
`;

// Per-property insights (the clickable Rendimiento cards in the property drawer).
export const PropertyAnalytics = gql`
  query propertyAnalytics($id: ID!, $from: ISO8601Date, $to: ISO8601Date, $timeZone: String) {
    propertyAnalytics(id: $id, from: $from, to: $to, timeZone: $timeZone) {
      totalVisits
      totalInquiries
      totalSearchAppearances
      daily { label count }
      dailySearches { label count }
      sources { label count }
      dailyInquiries { label count }
      topReferrers { count property { ${PROP} } }
    }
  }
`;

// Property owners across all the user's agencies (the owner-report picker).
export const PropertyOwners = gql`
  query propertyOwners {
    propertyOwners {
      id
      profile {
        name
        email
      }
    }
  }
`;

// Per-owner report (the homeowner export).
export const OwnerReport = gql`
  query ownerReport($ownerId: ID!, $from: ISO8601Date, $to: ISO8601Date, $timeZone: String) {
    ownerReport(ownerId: $ownerId, from: $from, to: $to, timeZone: $timeZone) {
      ownerName
      propertyCount
      totalVisits
      totalOpenInquiries
      totalInquiries
      daily { label count }
      properties {
        visits
        rangeVisits
        openInquiries
        totalInquiries
        property { id propertyType fullAddress state disabled }
      }
    }
  }
`;

// Agency-wide report (the global analytics view).
export const VisitReport = gql`
  query visitReport($from: ISO8601Date, $to: ISO8601Date, $timeZone: String, $operationType: String) {
    visitReport(from: $from, to: $to, timeZone: $timeZone, operationType: $operationType) {
      propertyCount
      totalVisits
      totalSearches
      totalAppearances
      daily { label count }
      dailySearches { label count }
      dailyAppearances { label count }
      sources { label count }
      topProperties { count property { ${PROP} } }
      openInquiries
      totalInquiries
      dailyInquiries { label count }
      topInquired { count property { ${PROP} } }
      medianFirstResponseDays
      unansweredOver5d
      recentInquiries {
        id
        name
        email
        phone
        topic
        message
        state
        createdAt
        visitorId
        leadTier
        leadScore
        property { ${PROP} }
      }
    }
  }
`;

// The Reportes "Demanda" table: every listing with its performance in the range,
// sorted and paged by the server (the metrics aren't columns, so they can't be
// sorted client-side without pulling the whole set).
export const PropertyPerformance = gql`
  query propertyPerformance(
    $from: ISO8601Date
    $to: ISO8601Date
    $timeZone: String
    $operationType: String
    $search: String
    $sort: String
    $dir: String
    $page: Int
    $per: Int
  ) {
    propertyPerformance(
      from: $from
      to: $to
      timeZone: $timeZone
      operationType: $operationType
      search: $search
      sort: $sort
      dir: $dir
      page: $page
      per: $per
    ) {
      totalCount
      pages
      rows {
        zone
        price
        visits
        rangeVisits
        inquiries
        conversion
        lastVisitAt
        daysSinceLastVisit
        property { ${PROP} }
      }
    }
  }
`;

// The Reportes "Propietarios" table: the agency-wide cartera, one row per listing.
export const OwnerPortfolio = gql`
  query ownerPortfolio(
    $from: ISO8601Date
    $to: ISO8601Date
    $timeZone: String
    $search: String
    $sort: String
    $dir: String
    $page: Int
    $per: Int
  ) {
    ownerPortfolio(
      from: $from
      to: $to
      timeZone: $timeZone
      search: $search
      sort: $sort
      dir: $dir
      page: $page
      per: $per
    ) {
      totalCount
      pages
      rows {
        ownerId
        ownerName
        published
        visits
        rangeVisits
        inquiries
        openInquiries
        property { ${PROP} }
      }
    }
  }
`;

// Agency-wide audience analytics (the Audiencia tab): who visits, what they
// search for, and demand vs supply.
export const AudienceReport = gql`
  query audienceReport($from: ISO8601Date, $to: ISO8601Date, $timeZone: String, $operationType: String) {
    audienceReport(from: $from, to: $to, timeZone: $timeZone, operationType: $operationType) {
      uniqueVisitors
      newVisitors
      returningVisitors
      identifiedVisitors
      anonymousVisitors
      devices { label count }
      totalSearches
      totalVisits
      totalEvents
      dailyVisitors { label count }
      dailySearches { label count }
      operationSplit { label count }
      categories { label count }
      topTypes { label count }
      topZones { label count }
      demandVsSupply { category demand supply }
      totalInquiries
      wonInquiries
      funnel { label count }
      conversion
      trends
      buyerLiquidity
      sellerLiquidity
      newListingsCount
      newListingsLiquidity
      timeToFirstInquiryDays
      weeklyInquiries { label count }
      zeroResultZones { label count }
      newUsers
      totalUsers
      dailyNewUsers { label count }
      signupsByMethod { label count }
      onboardingFunnel { label count }
    }
  }
`;

// The accounts explorer: registered client users with signup method + activity.
export const RegisteredUsers = gql`
  query registeredUsers($search: String, $limit: Int) {
    registeredUsers(search: $search, limit: $limit) {
      id
      name
      email
      phone
      registeredAt
      signupMethod
      leadId
      favoritesCount
      savedSearchesCount
      inquiriesCount
      lastActiveAt
    }
  }
`;

// Common visitor/lead shape (explorer rows + journey header).
const VISITOR = `
  id
  leadId
  deviceCount
  identified
  contactName
  contactEmail
  contactPhone
  firstSeen
  lastSeen
  device
  leadScore
  leadTier
  scoreBreakdown
  visitsCount
  searchesCount
  inquiriesCount
  topOperation
  topCategory
  topZone
  intent
`;

// Recent visitors/leads with derived intent (the audience explorer list).
export const AudienceVisitors = gql`
  query audienceVisitors($from: ISO8601Date, $to: ISO8601Date, $timeZone: String, $identifiedOnly: Boolean, $tier: String, $limit: Int) {
    audienceVisitors(from: $from, to: $to, timeZone: $timeZone, identifiedOnly: $identifiedOnly, tier: $tier, limit: $limit) {
      ${VISITOR}
    }
  }
`;

// A person's summary + merged, cross-device activity timeline (the drill-down).
// Pass leadId to pool every device; visitorId auto-expands to the person's lead.
const JOURNEY_EVENT = `kind at device title detail propertyId propertyAddress propertyType inquiryId meta`;
export const VisitorJourney = gql`
  query visitorJourney($visitorId: ID, $leadId: ID, $limit: Int) {
    visitorJourney(visitorId: $visitorId, leadId: $leadId, limit: $limit) {
      visitor { ${VISITOR} }
      events { ${JOURNEY_EVENT} }
      sessions {
        index
        startedAt
        endedAt
        device
        searches
        visits
        inquiries
        hasInquiry
        events { ${JOURNEY_EVENT} }
      }
    }
  }
`;

// Real-time presence — who's on the public site right now (the "En vivo" view).
export const LivePresence = gql`
  query livePresence {
    livePresence {
      total
      away
      onProperty
      identified
      devices { label count }
      visitors {
        visitorId
        visitorToken
        identified
        name
        leadTier
        leadScore
        device
        path
        pageLabel
        detail
        away
        secondsAgo
        property { id fullAddress propertyType }
      }
      topProperties { count property { id fullAddress propertyType } }
    }
  }
`;

// Most-visited areas (barrios + cities) and geo heat points for the demand map.
export const AreaReport = gql`
  query areaReport($from: ISO8601Date, $to: ISO8601Date, $timeZone: String, $operationType: String) {
    areaReport(from: $from, to: $to, timeZone: $timeZone, operationType: $operationType) {
      topNeighborhoods { city neighborhood visits inquiries }
      topCities { city visits inquiries }
      heatPoints { lat lng visits inquiries }
    }
  }
`;

// Demand sliced by budget band (views + search price filters) for "Demanda por presupuesto".
export const DemandByBudget = gql`
  query demandByBudget($operationType: String, $propertyType: String, $currency: String, $from: ISO8601Date, $to: ISO8601Date, $timeZone: String, $customMin: Int, $customMax: Int) {
    demandByBudget(operationType: $operationType, propertyType: $propertyType, currency: $currency, from: $from, to: $to, timeZone: $timeZone, customMin: $customMin, customMax: $customMax) {
      operationType
      currency
      totalViews
      totalSearches
      bands { label min max views searches }
      custom { label min max views searches }
    }
  }
`;

// Drill-down detail for one budget band (audience, source, geography, conversion).
export const BudgetSegment = gql`
  query budgetSegment($operationType: String, $propertyType: String, $currency: String, $from: ISO8601Date, $to: ISO8601Date, $timeZone: String, $min: Int, $max: Int) {
    budgetSegment(operationType: $operationType, propertyType: $propertyType, currency: $currency, from: $from, to: $to, timeZone: $timeZone, min: $min, max: $max) {
      supply
      totalVisits
      uniqueVisitors
      recurrentVisitors
      newVisitors
      returningVisitors
      inquiries
      sources { label count }
      types { label count }
      devices { label count }
      daily { label count }
      topBarrios { city neighborhood visits }
      topListings { count property { id fullAddress propertyType } }
      heatPoints { lat lng visits }
    }
  }
`;
